import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Heart, MapPin, CalendarDays, Clock, Send, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { getInvitation, sendRsvp, absUrl } from '../api';
import { THEMES } from '../themes';

const fmtDate = (d) => d ? new Date(d + (d.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '-';

function Countdown({ target, t }) {
  const [v, setV] = useState({});
  useEffect(() => {
    const tick = () => {
      const diff = new Date(target) - new Date();
      if (isNaN(diff) || diff <= 0) { setV({ d: 0, h: 0, m: 0, s: 0 }); return; }
      setV({ d: Math.floor(diff / 864e5), h: Math.floor(diff / 36e5) % 24, m: Math.floor(diff / 6e4) % 60, s: Math.floor(diff / 1e3) % 60 });
    };
    tick(); const i = setInterval(tick, 1000); return () => clearInterval(i);
  }, [target]);
  return (
    <div className="grid grid-cols-4 gap-2 sm:gap-3" data-testid="inv-countdown">
      {[['Hari', v.d], ['Jam', v.h], ['Menit', v.m], ['Detik', v.s]].map(([l, n]) => (
        <div key={l} className={`${t.card} backdrop-blur rounded-2xl py-3 border text-center`}>
          <div className={`font-serif text-2xl sm:text-3xl ${t.accentText}`}>{String(n ?? 0).padStart(2, '0')}</div>
          <div className={`text-[10px] tracking-widest uppercase ${t.muted}`}>{l}</div>
        </div>
      ))}
    </div>
  );
}

function EventCard({ icon: Icon, title, date, time, testid, t }) {
  return (
    <div className={`${t.card} rounded-3xl p-6 border flex-1`} data-testid={testid}>
      <div className={`w-10 h-10 rounded-full ${t.accentSoft} flex items-center justify-center mb-4`}><Icon className={`w-5 h-5 ${t.accentText}`} /></div>
      <div className={`text-xs tracking-[0.25em] mb-1 ${t.muted}`}>{title}</div>
      <div className={`font-serif text-xl ${t.text}`}>{fmtDate(date)}</div>
      {time && <div className={`flex items-center gap-1.5 text-sm mt-1 ${t.muted}`}><Clock className="w-3.5 h-3.5" /> {time} WIB</div>}
    </div>
  );
}

export default function Invitation() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const to = params.get('to') || '';
  const previewTheme = params.get('theme');
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: to, attending: true, headCount: 1, message: '' });
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { getInvitation(slug).then(setData).catch(() => setNotFound(true)); }, [slug]);

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (!form.name.trim()) { setErr('Nama wajib diisi'); return; }
    setBusy(true);
    try {
      const r = await sendRsvp(slug, { ...form, headCount: Number(form.headCount) || 1 });
      setSent(r);
      if (r.message) setData(d => ({ ...d, wishes: [r, ...d.wishes] }));
    } catch (e2) { setErr(e2?.response?.data?.detail || 'Gagal mengirim, coba lagi'); }
    finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-screen bg-cream-100 flex flex-col items-center justify-center px-5 text-center" data-testid="inv-not-found">
      <Heart className="w-10 h-10 text-terracotta-300 mb-4" />
      <h1 className="font-serif text-3xl text-sage-700 mb-2">Undangan tidak ditemukan</h1>
      <p className="text-sage-500 text-sm">Link yang kamu buka mungkin salah atau sudah tidak aktif.</p>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-cream-100 flex items-center justify-center text-sage-500">Memuat undangan...</div>;

  const c = data.couple;
  const t = THEMES[previewTheme] || THEMES[c.theme] || THEMES.klasik;
  const cover = absUrl(c.coverUrl);
  const mainDate = c.akadDate ? `${c.akadDate}T${c.akadTime || '08:00'}:00` : c.resepsiDate ? `${c.resepsiDate}T${c.resepsiTime || '10:00'}:00` : null;

  return (
    <div className={`min-h-screen ${t.bg} ${t.text}`} data-testid="invitation-page" data-theme={t.id}>
      <section className="relative overflow-hidden text-center">
        {cover ? (
          <div className="relative h-[62vh] min-h-[420px]" data-testid="inv-cover">
            <img src={cover} alt="Foto pasangan" className="absolute inset-0 w-full h-full object-cover" />
            <div className={`absolute inset-0 ${t.coverOverlay}`} />
          </div>
        ) : (
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `radial-gradient(circle at 20% 20%, ${t.dot1} 1.5px, transparent 1.5px), radial-gradient(circle at 80% 70%, ${t.dot2} 1.5px, transparent 1.5px)`, backgroundSize: '28px 28px' }} />
        )}
        <div className={`relative max-w-2xl mx-auto px-5 fade-in ${cover ? '-mt-40 pb-12' : 'pt-16 pb-12'}`}>
          <div className={`${t.accentText} text-xs tracking-[0.4em] font-medium mb-6`}>THE WEDDING OF</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-tight" data-testid="inv-couple-names">
            {c.brideName}<span className={`block ${t.accentText} text-3xl sm:text-4xl italic my-2`}>&amp;</span>{c.groomName}
          </h1>
          <div className="flex items-center justify-center gap-3 my-6">
            <div className={`h-px w-16 ${t.line}`} /><Heart className={`w-4 h-4 ${t.accentText}`} fill="currentColor" /><div className={`h-px w-16 ${t.line}`} />
          </div>
          <p className={t.muted}>{fmtDate(c.akadDate || c.resepsiDate)}</p>

          {to && (
            <div className={`mt-10 ${t.card} rounded-3xl p-6 border inline-block min-w-[260px]`} data-testid="inv-guest-greeting">
              <div className={`text-xs tracking-widest mb-1 ${t.muted}`}>KEPADA YTH.</div>
              <div className={`font-serif text-2xl ${t.text}`}>{to}</div>
              <div className={`text-xs mt-1 ${t.muted}`}>Tanpa mengurangi rasa hormat, kami mengundang Anda</div>
            </div>
          )}
        </div>
      </section>

      <section className="px-5 max-w-2xl mx-auto -mt-2">
        {mainDate && <Countdown target={mainDate} t={t} />}
      </section>

      <section className="px-5 py-12 max-w-2xl mx-auto text-center">
        <p className={`leading-relaxed italic font-serif text-lg ${t.textSoft}`} data-testid="inv-greeting-text">
          {c.greeting || 'Dengan memohon rahmat dan ridho Allah SWT, kami bermaksud menyelenggarakan pernikahan kami. Merupakan suatu kehormatan dan kebahagiaan bagi kami apabila Bapak/Ibu/Saudara/i berkenan hadir dan memberikan doa restu.'}
        </p>
      </section>

      <section className="px-5 max-w-2xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-4">
          {c.akadDate && <EventCard icon={CalendarDays} title="AKAD NIKAH" date={c.akadDate} time={c.akadTime} testid="inv-akad-card" t={t} />}
          {c.resepsiDate && <EventCard icon={Heart} title="RESEPSI" date={c.resepsiDate} time={c.resepsiTime} testid="inv-resepsi-card" t={t} />}
        </div>
        {(c.venue || c.venueAddress) && (
          <div className={`${t.venue} rounded-3xl p-7 mt-4 relative overflow-hidden`} data-testid="inv-venue-card">
            <div className={`absolute -right-8 -bottom-8 w-32 h-32 rounded-full ${t.venueBlob}`} />
            <div className="relative">
              <div className="flex items-center gap-2 text-xs tracking-[0.25em] opacity-80 mb-2"><MapPin className="w-4 h-4" /> LOKASI</div>
              <div className="font-serif text-2xl">{c.venue}</div>
              {c.venueAddress && <div className="opacity-80 text-sm mt-1">{c.venueAddress}</div>}
              {c.mapsUrl && <a href={c.mapsUrl} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 mt-5 ${t.venueBtn} rounded-full px-5 py-2.5 text-sm font-medium`} data-testid="inv-maps-link"><MapPin className="w-4 h-4" /> Buka Google Maps</a>}
            </div>
          </div>
        )}
      </section>

      <section className="px-5 py-12 max-w-2xl mx-auto">
        <div className={`${t.card} rounded-3xl p-7 border`}>
          <div className={`${t.accentText} text-xs tracking-[0.3em] font-medium mb-2`}>RSVP</div>
          <h2 className="font-serif text-3xl mb-1">Konfirmasi Kehadiran</h2>
          <p className={`text-sm mb-6 ${t.muted}`}>Bantu kami mempersiapkan hari istimewa ini dengan mengonfirmasi kehadiranmu.</p>
          {sent ? (
            <div className={`${t.accentSoft} rounded-2xl p-6 text-center`} data-testid="rsvp-success">
              <div className={`w-12 h-12 rounded-full ${t.button} mx-auto flex items-center justify-center mb-3`}><Check className="w-6 h-6" /></div>
              <div className="font-serif text-xl">Terima kasih, {sent.name}!</div>
              <div className={`text-sm mt-1 ${t.muted}`}>{sent.attending ? 'Sampai bertemu di hari bahagia kami 🤍' : 'Doa terbaikmu sangat berarti bagi kami 🤍'}</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="rsvp-form">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" className={`h-11 rounded-xl ${t.input}`} data-testid="rsvp-name-input" />
              <div className="grid grid-cols-2 gap-3">
                {[[true, 'Hadir', Check], [false, 'Tidak Hadir', X]].map(([v, l, I]) => (
                  <button type="button" key={l} onClick={() => setForm({ ...form, attending: v })} data-testid={v ? 'rsvp-attending-yes' : 'rsvp-attending-no'}
                    className={`h-11 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.attending === v ? t.chipOn : t.chipOff}`}>
                    <I className="w-4 h-4" /> {l}
                  </button>
                ))}
              </div>
              {form.attending && (
                <div className={`flex items-center gap-3 text-sm ${t.textSoft}`}>
                  <span>Jumlah orang</span>
                  <Input type="number" min="1" max="10" value={form.headCount} onChange={(e) => setForm({ ...form, headCount: e.target.value })} className={`w-20 h-10 rounded-xl ${t.input}`} data-testid="rsvp-headcount-input" />
                </div>
              )}
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tulis ucapan & doa untuk kedua mempelai..." className={`rounded-xl min-h-[90px] ${t.input}`} data-testid="rsvp-message-input" />
              {err && <p className="text-red-500 text-sm" data-testid="rsvp-error">{err}</p>}
              <Button type="submit" disabled={busy} className={`w-full h-11 rounded-full ${t.button}`} data-testid="rsvp-submit-button"><Send className="w-4 h-4 mr-2" /> {busy ? 'Mengirim...' : 'Kirim Konfirmasi'}</Button>
            </form>
          )}
        </div>
      </section>

      {data.wishes.length > 0 && (
        <section className="px-5 pb-12 max-w-2xl mx-auto" data-testid="inv-wishes">
          <h2 className="font-serif text-3xl mb-5 text-center">Ucapan &amp; Doa</h2>
          <div className="space-y-3">
            {data.wishes.map((w, i) => (
              <div key={i} className={`${t.card} rounded-2xl p-5 border`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-8 h-8 rounded-full ${t.accentSoft} flex items-center justify-center font-serif ${t.accentText}`}>{w.name.charAt(0).toUpperCase()}</div>
                  <div className="font-medium text-sm">{w.name}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.accentSoft} ${t.accentText}`}>{w.attending ? 'Hadir' : 'Tidak hadir'}</span>
                </div>
                <p className={`text-sm leading-relaxed ${t.textSoft}`}>{w.message}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <footer className={`text-center text-xs pb-10 px-5 ${t.muted}`}>
        Dibuat dengan <Heart className={`w-3 h-3 inline ${t.accentText}`} fill="currentColor" /> di <a href="/" className={`${t.accentText} font-medium`}>Naik Pelaminan</a>
      </footer>
    </div>
  );
}
